import apiClient from '@/lib/api-client';
import { UpdateForm } from '@/model/form/UpdateForm';
import { ApiError } from '@/model/ApiError';
import { PasswordFormData, ProfileFormData, User } from '@/model/Profile';
import {
    createSignupRequestFromFormData,
    SignupFormData,
    SignupRequest,
} from '@/model/Signup';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import Pageable from '@/model/Pageable';
import { PackageHistory } from '@/model/Project';
import { EventResponse } from '@/model/Event';
import { createDateFromUTCString } from '@/lib/date-util';

@Module({ namespaced: true, preserveState: false })
export default class AdminStorage extends VuexModule {
    error: ApiError | null = null;

    passwordError: ApiError | null = null;

    users: Pageable<User> | null = null;

    events: Pageable<EventResponse> | null = null;

    loading = false;

    historyLoading = false;

    @Mutation
    setLoading(newLoading: boolean): void {
        this.loading = newLoading;
    }

    @Mutation
    setActiveForUser(request: { userId: number; newActive: boolean }): void {
        this.users?.items.forEach((user) => {
            if (user.id === request.userId) {
                // eslint-disable-next-line no-param-reassign
                user.active = request.newActive;
            }
        });
    }

    @Mutation
    setHistoryLoading(newLoading: boolean): void {
        this.historyLoading = newLoading;
    }

    @Mutation
    setError(error: ApiError | null): void {
        this.error = error;
    }

    @Mutation
    setPasswordError(error: ApiError | null): void {
        this.passwordError = error;
    }

    @Mutation
    setPackageHistory(response: {
        id: number;
        newPackageHistory: PackageHistory[];
    }): void {
        this.users?.items.forEach((user) => {
            if (user.projectId === response.id) {
                const tmpPackageHistory = response.newPackageHistory;
                tmpPackageHistory.forEach((tmpPackage) => {
                    // eslint-disable-next-line no-param-reassign
                    tmpPackage.startedAt += 'Z';
                    // eslint-disable-next-line no-param-reassign
                    if (tmpPackage.endedAt) {
                        tmpPackage.endedAt += 'Z';
                    }
                });
                // eslint-disable-next-line no-param-reassign
                user.packageHistory = tmpPackageHistory;
            }
        });
    }

    @Mutation
    setUsers(newUsers: Pageable<User> | null): void {
        if (newUsers) {
            newUsers.items.forEach((user) => {
                user.fullName = `${user.firstName} ${user.lastName}`;
            });
        }
        this.users = newUsers;
    }

    @Mutation
    setEvents(events: Pageable<EventResponse> | null): void {
        if (events) {
            events.items.forEach((event) => {
                if (event.selectedStartDate) {
                    event.selectedStartDate = new Date(event.selectedStartDate);
                }

                if (event.startDate) {
                    event.startDate = new Date(event.startDate);
                }

                if (event.createdAt) {
                    event.createdAt = createDateFromUTCString(
                        event.createdAt.toString(),
                    );
                }

                if (event.deletedAt) {
                    event.deletedAt = createDateFromUTCString(
                        event.deletedAt.toString(),
                    );
                }
            });
        }
        this.events = events;
    }

    @Mutation
    deleteUser(id: number): void {
        if (this.users?.items) {
            this.users.items = this.users?.items.filter(
                (user) => user.id !== id,
            );
        }
    }

    @Action
    getAllUser(page: number): Promise<void> {
        this.context.commit('setLoading', true);
        return apiClient
            .get<number, Pageable<User>>('/users', {
                params: { page },
            })
            .then((data: Pageable<User>) => {
                this.context.commit('setUsers', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            })
            .finally(() => {
                this.context.commit('setLoading', false);
            });
    }

    @Action
    fetchEvents(page: number): Promise<void> {
        return apiClient
            .get<void, Pageable<EventResponse>>('/events/admin', {
                params: { page },
            })
            .then((data: Pageable<EventResponse>) => {
                this.context.commit('setEvents', data);
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    signup(formData: SignupFormData): Promise<void> {
        return apiClient
            .post<SignupRequest, void>(
                '/auth/signup',
                createSignupRequestFromFormData(formData),
            )
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    deactivateUserById(id: number): Promise<void> {
        return apiClient
            .delete(`/users/${id}/deactivate`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('setActiveForUser', {
                    userId: id,
                    newActive: false,
                });
                document.getElementById(`btn${id}`)?.blur();
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    reactivateUserById(id: number): Promise<void> {
        return apiClient
            .put(`/users/${id}/reactivate`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('setActiveForUser', {
                    userId: id,
                    newActive: true,
                });
                document.getElementById(`btn${id}`)?.blur();
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    deleteUserById(id: number): Promise<void> {
        return apiClient
            .delete(`/users/${id}`)
            .then(() => {
                this.context.commit('setError', null);
                this.context.commit('deleteUser', id);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updateProfile(request: UpdateForm<ProfileFormData>): Promise<void> {
        return apiClient
            .put(`/users/${request.id}`, request.formData)
            .then(() => {
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            });
    }

    @Action
    updatePassword(request: UpdateForm<PasswordFormData>): Promise<void> {
        return apiClient
            .put(`/users/${request.id}/password`, request.formData)
            .then(() => {
                this.context.commit('setPasswordError', null);
            })
            .catch((error) => {
                this.context.commit('setPasswordError', error);
                return Promise.reject();
            });
    }

    @Action
    fetchPackageHistory(projectId: number): Promise<void> {
        this.context.commit('setHistoryLoading', true);

        return apiClient
            .get(`/projects/${projectId}/package-history`)
            .then((data) => {
                this.context.commit('setPackageHistory', {
                    id: projectId,
                    newPackageHistory: data,
                });
                this.context.commit('setError', null);
            })
            .catch((error) => {
                this.context.commit('setError', error);
                return Promise.reject();
            })
            .finally(() => {
                this.context.commit('setHistoryLoading', false);
            });
    }

    get getError(): ApiError | null {
        return this.error;
    }

    get getPasswordError(): ApiError | null {
        return this.passwordError;
    }

    get getUsers(): Pageable<User> | null {
        return this.users;
    }

    get getEvents(): Pageable<EventResponse> | null {
        return this.events;
    }

    get getLoading(): boolean {
        return this.loading;
    }

    get getHistoryLoading(): boolean {
        return this.historyLoading;
    }
}
